// The tiered-scoring-teams board.
//
// Tiered teams cut across the walking groups on purpose — a foursome can walk together while
// each of them scores for a different team, drafted by handicap rather than by who signed up
// together. That means it needs its OWN table: the individual/group board above it answers
// "who is winning their group's card", this answers "who is winning the tournament's teams".
//
// Presentation only, same discipline as Standings.tsx — the ranking itself comes from
// `scoringTeamBoard` in the shared engine (the console) or `scoringTeamRowsFromPayload` (the
// public board), so the two screens read the same numbers. This file is used by both, which
// is why it carries its own tiny stylesheet rather than reaching for `.ev-*` or `.lb-*` —
// only one of those two sheets is ever loaded on a given page.

import { fmtToPar } from '@shared/sync/leaderboard';
import type { ScoringTeamRow } from '@shared/engine/tieredTeams';
import { tierLetter } from '../lib/scoringTeams';

export type TeamBoardRow = ScoringTeamRow;
type TeamBoardMember = ScoringTeamRow['members'][number];

/** "1" or "T1" — the engine gives every tied team the same numeric rank and leaves it to a
 *  reader to say "tied", the same convention `leaderboard.ts`'s own position string follows
 *  for the group board. */
function positionLabel(row: ScoringTeamRow, rows: ScoringTeamRow[]): string {
  if (row.position === null) return '—';
  const tied = rows.some((r) => r.id !== row.id && r.position === row.position);
  return tied ? `T${row.position}` : `${row.position}`;
}

function MemberLine({ members, showTiers }: { members: TeamBoardMember[]; showTiers: boolean }) {
  if (members.length === 0) return <span>No players yet</span>;
  return (
    <>
      {members.map((m, i) => (
        <span key={m.id}>
          {i > 0 ? ', ' : ''}
          {m.name}
          {showTiers ? <span className="tt-tier">{tierLetter(m.tier)}</span> : null}
        </span>
      ))}
    </>
  );
}

export function TeamStandings({
  rows,
  holeCount,
  headerLine,
  teeTimes,
  showTiers = true,
}: {
  rows: TeamBoardRow[];
  /** Team id to a formatted tee time ("2:10 PM"). Only set on tee-time events, where a
   *  player's first question at the join link is when their group goes off. */
  teeTimes?: Record<string, string>;
  /** Holes in this round, so a finished team reads "F" instead of a number. */
  holeCount: number;
  /** "Two best balls · gross" — said once, above the table, not per row. */
  headerLine: string;
  /**
   * Whether an A/B/C/D beside each name means anything here. False in SAME mode, where the
   * foursomes are the teams and nobody ever drafted a tiered roster — a letter there can
   * only be left over from an earlier draft, as Hawks View's was for two days before the
   * outing. Tiers still affect the order members are listed in; only the letter is dropped.
   */
  showTiers?: boolean;
}) {
  if (rows.length === 0) return null;
  const finishedAt = holeCount > 0 ? holeCount : Number.POSITIVE_INFINITY;

  return (
    <div className="card tt-card">
      <style>{`
.tt-head { margin: 0 0 10px; font-size: 13px; color: var(--ink-dim); font-weight: 600; }
.tt-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.tt-players { margin-top: 3px; color: var(--ink-dim); font-size: 12px; }
.tt-tier { display: inline-block; margin-left: 3px; font-weight: 800; color: var(--brass); }
.tt-captain { margin-top: 2px; color: var(--ink-dim); font-size: 11px; opacity: .75; }
`}</style>
      <p className="tt-head">{headerLine}</p>
      <div className="tt-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: 48 }}>Pos</th>
              <th>Team</th>
              <th style={{ width: 60 }}>Thru</th>
              <th style={{ width: 70 }}>To par</th>
              <th style={{ width: 70 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td className="tnum">{positionLabel(row, rows)}</td>
                <td>
                  <strong>{row.name}</strong>
                  {row.captainName ? <div className="tt-captain">Capt. {row.captainName}</div> : null}
                  {teeTimes?.[row.id] ? <div className="tt-captain">Tee {teeTimes[row.id]}</div> : null}
                  <div className="tt-players">
                    <MemberLine members={row.members} showTiers={showTiers} />
                  </div>
                </td>
                <td className="tnum">
                  {row.thru <= 0 ? '—' : row.thru >= finishedAt ? 'F' : row.thru}
                </td>
                <td className="tnum">{row.thru > 0 ? fmtToPar(row.toPar) : '—'}</td>
                <td className="tnum">{row.thru > 0 ? row.total : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default TeamStandings;
